import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { BadgePill, Text } from '@connect-x-app/hybrid-ui';
import { WorkstationState } from '@connect-x-app/shared-domain-models';
import { COLORS } from '@connect-x-app/shared-ui';
import { getWorkstationPillKey, mapAppliedWorkstation, WorkstationLabelMap } from '@connect-x-app/shared-utils';
import { FLIGHTROOM_TEST_IDS } from '@connect-x-app/web-test-ids';
import { CollapsibleView } from '@connect-x-app/web-ui';

import './WorkstationCollapse.scss';

type Props = {
  handleWorkstationClick: () => void;
  workstation: WorkstationState | null;
};

export const WorkstationCollapse = memo(({ handleWorkstationClick, workstation }: Props) => {
  const { t } = useTranslation();

  const workstationPills = mapAppliedWorkstation(workstation).map(pill => {
    return { ...pill, value: t(WorkstationLabelMap.get(pill.key)!, { value: pill.value }) };
  });

  const renderWorkstationPill = ({ key, label, value }: { key: keyof WorkstationState; label: string; value: string }) => {
    const uniqueKey = getWorkstationPillKey(key, label);
    return (
      <BadgePill
        key={uniqueKey}
        onClick={handleWorkstationClick}
        size="xs"
        testId={FLIGHTROOM_TEST_IDS.workstationCollapse.pill(uniqueKey)}
        text={value}
        type="primary"
      />
    );
  };

  if (!workstationPills.length) {
    return null;
  }

  const hasMultiplePills: boolean = workstationPills.length > 1;

  const headerContent = (
    <div className="header-content">
      <Text size="SM" uppercase weight="bold">
        {t('WORKSTATION.LABEL.WORKSTATION')}
      </Text>
      {hasMultiplePills ? (
        <Text className="applied-text" color={COLORS.zulu.$08} size="SM" weight="semiBold">
          {t('WORKSTATION.LABEL.APPLIED', { count: workstationPills.length })}
        </Text>
      ) : null}
    </div>
  );

  return (
    <div className="workstation-collapse">
      {hasMultiplePills ? (
        <CollapsibleView expanded headerContent={headerContent} testId={FLIGHTROOM_TEST_IDS.workstationCollapse.collapsible}>
          {workstationPills.map(renderWorkstationPill)}
        </CollapsibleView>
      ) : (
        <div className="single-pill-container" data-test={FLIGHTROOM_TEST_IDS.workstationCollapse.singlePillContainer}>
          {headerContent}
          {workstationPills.map(renderWorkstationPill)}
        </div>
      )}
    </div>
  );
});
